import { useMemo, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  PaginationContext,
  FilterContext,
  MarketingFiles as MarketingFilesComponent,
} from "dash-ui";
import { DEFAULT_ROOT_MEDIA_FOLDER_NAME } from "dash-common";
import {
  GetPropertyByIdQuery,
  useGetAllFilesInPropertyPreviewQuery,
  useGetAllFoldersInPropertyQuery,
  useGetTopLevelFoldersInPropertyQuery,
} from "gql/generated.graphql";
import { RoutesPath } from "flow-components/routes/routes";
import qs from "qs";

interface MarketingFilesProps {
  property: GetPropertyByIdQuery["getPropertyById"];
  allowDownloads?: boolean;
  isSeeker?: boolean;
}

const MarketingFiles = ({
  property,
  allowDownloads,
  isSeeker,
}: MarketingFilesProps) => {
  const propertyId = property._id;
  const navigate = useNavigate();
  const location = useLocation();
  const {
    state: paginationState,
    getRefetchProps,
    setTotal,
  } = useContext(PaginationContext);
  const { limit } = getRefetchProps(paginationState);
  const { state: filterState } = useContext(FilterContext);

  const { data: foldersData, loading: foldersLoading } =
    useGetTopLevelFoldersInPropertyQuery({
      variables: { propertyId: property._id },
    });

  const { folderId: parentFolderId } = useMemo(
    () => qs.parse(location.search.slice(1)),
    [location]
  );

  const tabsFolders = useMemo(
    () => foldersData?.getTopLevelFoldersInProperty || [],
    [foldersData]
  );

  const mediaFolderId = useMemo(
    () => parentFolderId || (tabsFolders.length > 0 ? tabsFolders[0]._id : ""),
    [parentFolderId, tabsFolders]
  );

  const { data: innerFoldersData, loading: innerFoldersLoading } =
    useGetAllFoldersInPropertyQuery({
      variables: {
        propertyId: property._id,
        mediaFolderId,
      },
      skip: !!tabsFolders.some(
        (item) =>
          item._id === mediaFolderId &&
          item.folderName === DEFAULT_ROOT_MEDIA_FOLDER_NAME
      ),
    });

  const innerFolders = useMemo(
    () => innerFoldersData?.getAllFoldersInProperty || [],
    [innerFoldersData]
  );

  const allFilesListVariables = useMemo(
    () => ({
      pagination: { limit },
      filter: {
        propertyId,
        mediaFolderIds:
          filterState.foldersIds?.length > 0
            ? filterState.foldersIds
            : mediaFolderId
            ? [mediaFolderId, ...innerFolders.map((f) => f._id)]
            : innerFolders.map((f) => f._id),
      },
      previewAsSeeker: isSeeker,
    }),
    [
      limit,
      propertyId,
      mediaFolderId,
      innerFolders,
      isSeeker,
      filterState.foldersIds,
    ]
  );

  const {
    data: allFilesInProperty,
    loading: childFoldersFilesLoading,
    fetchMore: fetchMoreChildFoldersFiles,
  } = useGetAllFilesInPropertyPreviewQuery({
    variables: allFilesListVariables,
    fetchPolicy: "network-only",
  });

  const { allFilesCollection, allFilesCollectionTotal } = useMemo(
    () => ({
      allFilesCollection:
        allFilesInProperty?.getAllFilesInPropertyPreview.filesCollection || [],
      allFilesCollectionTotal:
        allFilesInProperty?.getAllFilesInPropertyPreview.filesTotalCount || 0,
    }),
    [allFilesInProperty]
  );

  useEffect(() => {
    if (allFilesCollectionTotal > 0) {
      setTotal(allFilesCollectionTotal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFilesCollectionTotal]);

  const files = useMemo(
    () =>
      allFilesCollection.map((item) => {
        const file = item.files[0];
        return {
          _id: item._id,
          name: file.name,
          size: item.sizeInBytes,
          publicUrl: file.publicUrl,
          thumbnailFileUrl: file.thumbnailFileUrl,
          type: file.type,
        };
      }),
    [allFilesCollection]
  );

  // Determine the current route based on isSeeker prop
  const currentRoute = isSeeker
    ? RoutesPath.PropertyDirectPreview.replace(":id", propertyId)
    : RoutesPath.PropertyAgentPreview.replace(":id", propertyId);

  return (
    <MarketingFilesComponent
      tabs={tabsFolders.map((item) => ({
        label: item.folderName,
        key: item._id,
      }))}
      files={files as any}
      folders={innerFolders as any}
      activeTab={mediaFolderId as string}
      isPortfolioView={true}
      onDownloadClick={allowDownloads !== false ? undefined : undefined}
      loading={
        foldersLoading || childFoldersFilesLoading || innerFoldersLoading
      }
      fetchMoreProps={{
        fetchMore: fetchMoreChildFoldersFiles,
        variables: allFilesListVariables,
        queryName: "getAllFilesInPropertyPreview",
        collectionName: "filesCollection",
      }}
      setActiveTab={(tab) => navigate(currentRoute + `?folderId=${tab}`)}
      allowDownloads={allowDownloads}
      isSeeker={isSeeker}
    />
  );
};

export default MarketingFiles;
